import { Button } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getUserInfoService,
} from "../../../infrastructure/services/UserInfoService";
import {
  confirmFollowService,
  redirectNotificationService,
  seenNotificationService,
} from "../../../infrastructure/services/NotificationsService";
import { LangContext, UiTextsContext, UserDataContext } from "../../contexts/Contexts";
import {
  chatUserPath,
  eventDetailsPath,
  publicationDetailPath,
  postWasNotFoundPath,
  userPassportPath,
  userPath,
} from "../../routes/routes";
import { resolveNotificationTextKey } from "../../../shared/utils/translations/eventNotificationTexts";
import "../../styles/buttons.css";
import "../../styles/notifications.css";
import { formatDistanceToNow, isValid } from "date-fns";
import { enUS } from "date-fns/locale";
import { es } from "date-fns/locale";

const notificationCopy = {
  en: {
    system: "System",
    connectionRequest: "sent you a connection request",
    requestAccepted: "request accepted",
    requestDeclined: "request declined",
    publicationUpdate: "Publication update",
    openPublication: "Open publication details.",
  },
  es: {
    system: "Sistema",
    connectionRequest: "te envió una solicitud de conexión",
    requestAccepted: "solicitud aceptada",
    requestDeclined: "solicitud rechazada",
    publicationUpdate: "Actualización de publicación",
    openPublication: "Abrir detalles de la publicación.",
  },
};

const normalizeNotificationKind = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

// VIVA-1458 — que notificaciones llevan al detalle de un evento.
//
// Antes el unico camino a `/events/:eventId` era que el `mensaje` contuviera
// LB0728 o LB0730, que son las claves de la notificacion de asistencia. Todo lo
// demas caia al servicio de redireccion de publicaciones, que buscaba una
// publicacion con el id de un evento, no la encontraba y mandaba al usuario a
// /postWasNotFound. Decidirlo por el TIPO en vez de por una clave de texto es lo
// que hace que reaccion, comentario y moderacion lleguen a donde deben.
//
// En estas notificaciones `idPublicacion` trae el idEvento, no el de una
// publicacion: comprobado en pre el 29/09/2026 contra /api/vnotify/gral, donde
// la REACCION_EVENTO del usuario 15e4c1f7-... trae el mismo
// f0ccf51b-ce25-4b5f-bdd6-16bf0aef3527 que la URL del evento.
const EVENT_NOTIFICATION_KINDS = new Set([
  "reaccion_evento",
  "reaccion_comentario",
  "comentario_evento",
  "moderacion_comentario",
  "asistencia",
]);

const isEventNotification = (notification) => {
  if (
    EVENT_NOTIFICATION_KINDS.has(
      normalizeNotificationKind(notification?.notificationKind),
    )
  ) {
    return true;
  }

  // Respaldo por texto para no perder lo que ya funcionaba si llega un tipo con
  // otro nombre: estas dos claves son las de la notificacion de asistencia.
  const content = notification?.content ?? "";
  return content.includes("LB0728") || content.includes("LB0730");
};

const sanitizeNotificationText = (value = "") =>
  String(value)
    // Remove replacement chars produced by malformed encoding.
    .replaceAll("\uFFFD", "")
    .replace(/\s{2,}/g, " ")
    .trim();

export const Notification = ({ notification }) => {
  const { userData } = useContext(UserDataContext);
  const { texts } = useContext(UiTextsContext);
  const { lang } = useContext(LangContext);
  const isSpanish = lang?.toLowerCase?.().startsWith("es");
  const copy = notificationCopy[isSpanish ? "es" : "en"];

  const navigate = useNavigate();

  const [userInfo, setUserInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [rejected, setRejected] = useState(false);

  const markNotificationAsSeen = async () => {
    if (
      notification?.seen ||
      !notification?.notificationId ||
      !userData?.idUsuario
    ) {
      return;
    }

    try {
      await seenNotificationService({
        IdNotificacion: notification.notificationId,
        IdUsuario: userData.idUsuario,
      });

   
      window.dispatchEvent(new CustomEvent("notifications:refresh-all"));
      window.dispatchEvent(new CustomEvent("notifications:tab-refresh"));
    } catch (error) {
      console.error("Failed mark notification as read", error);
    }
  };

  useEffect(() => {
    if (
      notification?.isAttendanceNotification ||
      !notification?.otherUserId ||
      notification?.nombreUsuario?.trim?.()
    ) {
      return;
    }

    getUserInfo(notification.otherUserId);
  }, [notification]);

  //get user info follow request
  const getUserInfo = async (user) => {
    try {
      const info = await getUserInfoService(user);
      setUserInfo(info);
    } catch (error) {
      console.error("Problem while getting user info");
    }
  };

  const notificationTime = (() => {
    const date = new Date(notification?.timestamp);

    if (!notification?.timestamp || !isValid(date)) return null;

    return formatDistanceToNow(date, {
      addSuffix: true,
      locale: isSpanish ? es : enUS,
    });
  })();

  const displayName = notification?.nombreUsuario?.trim?.()
    || (userInfo?.apellidos
      ? `${userInfo.nombres} ${userInfo.apellidos}`
      : userInfo?.nombre || userInfo?.nombres)
    || copy.system;
  const shouldHideSender = Boolean(notification?.isAttendanceNotification);

  const parseNotificationContent = (content) => {
    if (!content) return "";
    const safeContent = sanitizeNotificationText(content);
    
    // Dividir por comas o puntos, luego limpiar cada parte
    return safeContent.split(/[,.]/).reduce((acc, part, index) => {
      const trimmedPart = part.trim();
      const separator = index === 0 ? "" : " "; // No agregar espacio al principio
      
      if (trimmedPart.startsWith("LB")) {
        // VIVA-1456 / VIVA-1457: el esquema remoto gana; el respaldo local solo
        // cubre las claves de eventos que todavia no publica (LB1160 a LB1164).
        const resolved = resolveNotificationTextKey(
          trimmedPart,
          texts,
          isSpanish,
        );
        if (resolved) return acc + separator + resolved;
      }
      return acc + separator + trimmedPart;
    }, "");
  };

  const parsedContent = parseNotificationContent(notification.content);
  const contentWithoutName =
    displayName !== copy.system && parsedContent.startsWith(displayName)
      ? parsedContent.slice(displayName.length).trim()
      : parsedContent;
  const isPublicationNotification = Boolean(
    notification?.idPublicacion &&
      !notification.type &&
      !notification?.isChatReaction &&
      !notification?.content?.includes?.("LB0728") &&
      !notification?.content?.includes?.("LB0730"),
  );

  // VIVA-1456: `titulo` tambien llega como clave del esquema (LB1143, LB1161...).
  // Antes se pintaba cruda, asi que la tarjeta de una reaccion a un evento decia
  // literalmente "LB1161". Se resuelve por el mismo camino que el cuerpo.
  const parsedTitle = (() => {
    const rawTitle = notification?.title?.trim?.() ?? "";
    if (!rawTitle.startsWith("LB")) return rawTitle;
    return resolveNotificationTextKey(rawTitle, texts, isSpanish) ?? rawTitle;
  })();

  const publicationSummary = (
    contentWithoutName || parsedContent || parsedTitle || ""
  ).trim();

  const publicationPreview = publicationSummary.length > 120
    ? `${publicationSummary.slice(0, 117)}...`
    : publicationSummary;

  const publicationMetaLabel = parsedTitle || copy.publicationUpdate;

  //accept follow request
  const onClickAccept = async () => {
    try {
      setLoading(true);
      await markNotificationAsSeen();
      const req = {
        IdUsuario: userData?.idUsuario,
        IdAmigo: notification.otherUserId,
        Estatus: "A",
      };
      await confirmFollowService(req);
      setAccepted(true);
    } catch (error) {
      console.error("Failed follow user");
    } finally {
      setLoading(false);
    }
  };

  //reject follow request
  const onClickReject = async () => {
    try {
      setLoading(true);
      await markNotificationAsSeen();
      const req = {
        IdUsuario: userData?.idUsuario,
        IdAmigo: notification.otherUserId,
        Estatus: "R",
      };
      await confirmFollowService(req);
      setRejected(true);
    } catch (error) {
      console.error("Failed reject user");
    } finally {
      setLoading(false);
    }
  };

  const navigateToChatReaction = () => {
    if (!notification?.otherUserId) {
      return false;
    }

    const messageId = notification?.idMensaje ?? null;

    navigate(chatUserPath, {
      state: {
        idUsuario: notification.otherUserId,
        idChat: notification?.idChat ?? null,
        nombres: notification?.nombreUsuario ?? "",
        profPic: notification?.fotoPerfilBase64 ?? null,
        scrollTarget: messageId
          ? {
              id: `notification-${notification.notificationId ?? messageId}`,
              type: "message-preview",
              messageId,
            }
          : null,
      },
    });

    return true;
  };

  // Redireccionamiento de notificación
  const onClickRedirect = async () => {
    setLoading(true);
    try {
      await markNotificationAsSeen();

      if (notification?.isChatReaction && navigateToChatReaction()) {
        return;
      }

      if (
        normalizeNotificationKind(notification?.notificationKind) ===
        "amistadaceptada"
      ) {
        const profileUserId =
          notification?.redirectUserId || notification?.otherUserId;

        if (profileUserId) {
          navigate(userPath, {
            state: {
              idUsuario: profileUserId,
              nombres: notification?.nombreUsuario ?? "",
              nombre: notification?.nombreUsuario ?? "",
              fotoPerfilBase64: notification?.fotoPerfilBase64 ?? null,
            },
          });
          return;
        }
      }

      if (
        notification.content === "Felicidades, eres usuario verificado." ||
        notification.content === "Felicidades, eres Asociado Comercial."
      ) {
        navigate(userPassportPath);
        return;
      } else if (isEventNotification(notification) && notification?.idPublicacion) {
        navigate(eventDetailsPath.replace(":eventId", notification.idPublicacion));
        return;
      }

      const req = {
        userId: userData?.idUsuario,
        publicationId: notification?.idPublicacion,
      };
      const response = await redirectNotificationService(req);
      if (
        !response?.idPublicacion ||
        response?.idPublicacion === "00000000-0000-0000-0000-000000000000"
      ) {
        navigate(postWasNotFoundPath);
        return;
      } else {
        navigate(`${publicationDetailPath}?publication=${response?.idPublicacion}`, {
          state: {
            publication: response,
          },
        });
      }
    } catch (error) {
      if (
        error?.status === 404 ||
        error?.response?.status === 404 ||
        error?.data?.message === "No se encontró la publicación."
      ) {
        navigate(postWasNotFoundPath);
        return;
      }

      console.error("Failed redirect publication");
    } finally {
      setLoading(false);
    }
  };


  if (notification.type && !accepted && !rejected) {
    return (
      <div className="notification-card notification-card--actionable">
        <div className="notification-card__avatar-wrap">
          <img
            src={notification?.fotoPerfilBase64 || "/img/icons/UserFalse.svg"}
            alt={displayName}
            className="notification-card__avatar"
          />
        </div>

        <div className="notification-card__body">
          <p className="notification-card__text">
            <strong>{displayName}</strong>{" "}
            {contentWithoutName || copy.connectionRequest}
          </p>
          {notificationTime ? (
            <span className="notification-card__time">{notificationTime}</span>
          ) : null}

          <div className="notification-card__actions">
            <Button
              className={"accept-button"}
              onClick={onClickAccept}
              loading={loading}
            >
              {texts?.LB0151 || "Accept"}
            </Button>
            <Button
              className={"reject-button"}
              onClick={onClickReject}
              loading={loading}
            >
              {texts?.LB0152 || "Decline"}
            </Button>
          </div>
        </div>

        {!notification?.seen ? <span className="notification-card__dot" /> : null}
      </div>
    );
  }

  if (notification.type && (accepted || rejected)) {
    return (
      <div className="notification-card">
        <div className="notification-card__avatar-wrap">
          <img
            src={notification?.fotoPerfilBase64 || "/img/icons/UserFalse.svg"}
            alt={displayName}
            className="notification-card__avatar"
          />
        </div>

        <div className="notification-card__body">
          <p className="notification-card__text">
            <strong>{displayName}</strong>{" "}
            {accepted ? copy.requestAccepted : copy.requestDeclined}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onClickRedirect}
      className={`notification-card notification-card--clickable ${
        isPublicationNotification ? "notification-card--publication" : ""
      } ${
        notification?.isBlockedPublication ? "notification-card--blocked-publication" : ""
      }`}
    >
      <div className="notification-card__avatar-wrap">
        {notification.otherUserId || shouldHideSender ? (
          <>
            <img
              src={notification?.fotoPerfilBase64 || "/img/icons/UserFalse.svg"}
              alt={shouldHideSender ? copy.system : displayName}
              className="notification-card__avatar"
            />
          </>
        ) : (
          <div className="notification-card__system-icon">
            <InfoCircleOutlined />
          </div>
        )}
      </div>

      <div className="notification-card__body">
        {isPublicationNotification ? (
          <>
            <div className="notification-publication__meta">
              {notification.otherUserId ? (
                <>
                  <strong>{displayName}</strong>{" "}
                </>
              ) : (
                <span>{displayName}</span>
              )}
            </div>
            <div className="notification-publication__preview">
              {publicationPreview || copy.openPublication}
            </div>
          </>
        ) : (
          <p className="notification-card__text">
            {shouldHideSender ? (
              parsedContent
            ) : notification.otherUserId ? (
              <>
                <strong>{displayName}</strong>{" "}
                {contentWithoutName || parsedContent}
              </>
            ) : (
              parsedContent
            )}
          </p>
        )}
        {notificationTime ? (
          <span className="notification-card__time">{notificationTime}</span>
        ) : null}
      </div>

      {!notification?.seen ? <span className="notification-card__dot" /> : null}
    </div>
  );
};
